import { apiServiceInstance } from '@/core/helpers/utils';
import { extractFilterDateRange } from '@/core/helpers/filter-date-range';
import { extractWorkingYears } from '@/core/helpers/working-years';
import type { IAbilitiesResponse } from '@/core/interfaces/ApiResponses';
import store from '@/store';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { AxiosResponse } from 'axios';
// used as @Module/@Action/@Mutation decorators
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';

type AuthzCommitContext = {
  commit: (mutation: Mutations, payload?: unknown) => void;
};

function getAuthzModuleState(): AuthzModule {
  return (store.state as { AuthzModule: AuthzModule }).AuthzModule;
}

/**
 * Helper function to generate cache key from userId and version
 */
function getCacheKey(userId: string | null, version: string | null): string {
  return `${userId || 'anonymous'}:${version || 'default'}`;
}

/**
 * Helper function to generate localStorage cache key
 */
function getLocalStorageCacheKey(
  userId: string | number | null,
  version: number | null,
): string {
  return `abilities:${userId || 'anonymous'}:${version ?? 'default'}`;
}

export type AbilitiesPayload = {
  permissions: string[];
  version: number;
  userId: string | number;
};

// Module-level variable to track pending promise (not in Vuex state)
let pendingLoadPromise: Promise<void> | null = null;

function findLatestCachedAbilities(
  userId: string | number,
): AbilitiesPayload | null {
  const prefix = `abilities:${userId}:`;
  let latest: AbilitiesPayload | null = null;

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith(prefix)) {
      continue;
    }

    try {
      const data = JSON.parse(
        localStorage.getItem(key) || '',
      ) as AbilitiesPayload;
      if (!data?.permissions || data.version == null) {
        continue;
      }
      if (!latest || data.version > latest.version) {
        latest = data;
      }
    } catch {
      // Invalid cache entry, ignore
    }
  }

  return latest;
}

function pruneStaleAbilitiesCache(
  userId: string | number,
  keepVersion: number,
): void {
  const prefix = `abilities:${userId}:`;
  const keepKey = getLocalStorageCacheKey(userId, keepVersion);
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(prefix) && key !== keepKey) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

function parseAbilitiesResponse(
  data: IAbilitiesResponse | string[] | undefined,
  fallbackUserId: string | null,
): AbilitiesPayload | null {
  if (!data) {
    return null;
  }

  if (Array.isArray(data)) {
    if (!fallbackUserId) {
      return null;
    }
    return {
      permissions: data,
      version: 0,
      userId: fallbackUserId,
    };
  }

  if (Array.isArray(data.permissions)) {
    return {
      permissions: data.permissions,
      version: data.version,
      userId: data.userId ?? fallbackUserId ?? 'anonymous',
    };
  }

  return null;
}

function versionToString(version: number | null | undefined): string | null {
  return version == null ? null : String(version);
}

function shouldInvalidateForPayload(
  moduleState: AuthzModule,
  payload: AbilitiesPayload,
): boolean {
  const payloadUserId = payload.userId?.toString() || null;
  const payloadVersion = versionToString(payload.version);

  return (
    moduleState.userId !== payloadUserId ||
    moduleState.version !== payloadVersion
  );
}

function permissionsDiffer(
  current: Set<string>,
  next: string[],
): boolean {
  if (current.size !== next.length) {
    return true;
  }

  return next.some((perm) => !current.has(perm));
}

function applyAbilitiesPayload(
  context: AuthzCommitContext,
  moduleState: AuthzModule,
  payload: AbilitiesPayload,
) {
  context.commit(Mutations.SET_USER_ID, payload.userId?.toString() || null);
  context.commit(Mutations.SET_VERSION, versionToString(payload.version));
  context.commit(Mutations.SET_ABILITIES, payload.permissions || []);
  context.commit(Mutations.SET_ABILITIES_LOADED, true);

  const memoryCacheKey = getCacheKey(
    payload.userId?.toString() || null,
    versionToString(payload.version),
  );
  moduleState.cache.set(memoryCacheKey, new Set(payload.permissions || []));
}

function persistAbilitiesPayload(payload: AbilitiesPayload) {
  if (payload.userId == null || payload.version == null) {
    return;
  }

  const cacheKey = getLocalStorageCacheKey(payload.userId, payload.version);
  localStorage.setItem(cacheKey, JSON.stringify(payload));
  pruneStaleAbilitiesCache(payload.userId, payload.version);
}

function resetAbilitiesInMemoryState(
  context: AuthzCommitContext,
  moduleState: AuthzModule,
) {
  context.commit(Mutations.SET_ABILITIES, []);
  context.commit(Mutations.SET_ABILITIES_LOADED, false);
  context.commit(Mutations.SET_USER_ID, null);
  context.commit(Mutations.SET_VERSION, null);
  context.commit(Mutations.SET_DON_VI_OPTIONS, {});
  context.commit(Mutations.SET_ABILITIES_ID_DON_VI, null);
  moduleState.cache.clear();
}

function extractDonViOptionsFromResponse(
  data: IAbilitiesResponse | string[] | undefined,
): { options: Record<string, string>; idDonVi: number | null } {
  if (!data || Array.isArray(data)) {
    return { options: {}, idDonVi: null };
  }

  const options =
    data.options && typeof data.options === 'object' && !Array.isArray(data.options)
      ? { ...data.options }
      : {};

  const idDonVi =
    data.id_don_vi != null && !Number.isNaN(Number(data.id_don_vi))
      ? Number(data.id_don_vi)
      : null;

  return { options, idDonVi };
}

function applyDonViOptionsFromLiveResponse(
  context: AuthzCommitContext,
  data: IAbilitiesResponse | string[] | undefined,
) {
  const { options, idDonVi } = extractDonViOptionsFromResponse(data);
  context.commit(Mutations.SET_DON_VI_OPTIONS, options);
  context.commit(Mutations.SET_ABILITIES_ID_DON_VI, idDonVi);
}

type FetchAbilitiesOptions = {
  /** Apply warm cache from localStorage before hitting the API */
  useWarmCache?: boolean;
  /** Re-apply abilities even when already loaded */
  force?: boolean;
  /** Always apply a successful API payload (used by SYNC before each request) */
  alwaysApplyFromApi?: boolean;
};

async function fetchAbilitiesFromApi(
  context: AuthzCommitContext,
  moduleState: AuthzModule,
  options: FetchAbilitiesOptions = {},
): Promise<void> {
  const { useWarmCache = true, force = false, alwaysApplyFromApi = false } =
    options;

  const rootState = store.state as {
    AuthModule?: {
      user?: { id?: number | string; permissions?: string[] };
      currentUser?: { id?: number | string; permissions?: string[] };
    };
  };
  const authModule = rootState.AuthModule;
  const currentUser = authModule?.user || authModule?.currentUser;
  const newUserId = currentUser?.id?.toString() || null;

  const userIdChanged =
    moduleState.userId !== null &&
    newUserId !== null &&
    moduleState.userId !== newUserId;

  if (userIdChanged) {
    resetAbilitiesInMemoryState(context, moduleState);
  }

  if (useWarmCache && newUserId) {
    const warmCache = findLatestCachedAbilities(newUserId);
    if (warmCache) {
      const versionChanged = shouldInvalidateForPayload(moduleState, warmCache);
      if (versionChanged || !moduleState.loaded) {
        applyAbilitiesPayload(context, moduleState, warmCache);
      }
    }
  }

  try {
    const response: AxiosResponse<{
      data: IAbilitiesResponse | string[];
    }> = await apiServiceInstance().vueInstance.axios.get(
      '/api/v1/me/abilities',
      { meta: { skipAbilitiesSync: true } },
    );

    const responseData = response.data?.data;
    const selectedYear = (store.state as { AuthModule?: { selectedYear?: string | null } })
      .AuthModule?.selectedYear;
    const filterRange = extractFilterDateRange(
      responseData,
      selectedYear ? Number(selectedYear) : undefined
    );
    if (filterRange) {
      context.commit(Mutations.SET_FILTER_DATE_RANGE, filterRange);
    }

    const workingYears = extractWorkingYears(responseData);
    if (workingYears?.length) {
      context.commit(Mutations.SET_WORKING_YEARS, workingYears);
    }

    // DonVi options are fresh each request (not in permissions cache)
    applyDonViOptionsFromLiveResponse(context, responseData);

    const parsed = parseAbilitiesResponse(responseData, newUserId);
    const currentState = getAuthzModuleState();

    if (parsed) {
      const versionChanged = shouldInvalidateForPayload(currentState, parsed);
      const contentChanged = permissionsDiffer(
        currentState.permissions,
        parsed.permissions,
      );

      if (
        alwaysApplyFromApi ||
        versionChanged ||
        !currentState.loaded ||
        force ||
        contentChanged
      ) {
        applyAbilitiesPayload(context, moduleState, parsed);
        persistAbilitiesPayload(parsed);
      }
    } else if (currentUser?.permissions && Array.isArray(currentUser.permissions)) {
      applyAbilitiesPayload(context, moduleState, {
        permissions: currentUser.permissions,
        version: Number(currentState.version) || 0,
        userId: newUserId || 'anonymous',
      });
    } else {
      applyAbilitiesPayload(context, moduleState, {
        permissions: [],
        version: 0,
        userId: newUserId || 'anonymous',
      });
    }
  } catch {
    const currentState = getAuthzModuleState();
    if (!currentState.loaded) {
      if (currentUser?.permissions && Array.isArray(currentUser.permissions)) {
        applyAbilitiesPayload(context, moduleState, {
          permissions: currentUser.permissions,
          version: Number(currentState.version) || 0,
          userId: newUserId || 'anonymous',
        });
      } else {
        applyAbilitiesPayload(context, moduleState, {
          permissions: [],
          version: 0,
          userId: newUserId || 'anonymous',
        });
      }
    }
  }
}

async function runAbilitiesTask(
  context: AuthzCommitContext,
  moduleState: AuthzModule,
  options: FetchAbilitiesOptions,
): Promise<void> {
  if (pendingLoadPromise) {
    return pendingLoadPromise;
  }

  const loadPromise = fetchAbilitiesFromApi(context, moduleState, options);
  pendingLoadPromise = loadPromise;

  try {
    await loadPromise;
  } finally {
    pendingLoadPromise = null;
  }
}

/**
 * @description Authorization module for managing user permissions
 * This is the SINGLE SOURCE OF TRUTH for loading and validating permissions.
 * All callers (router guard, axios interceptor, directives, components) must use this module.
 */
@Module
export default class AuthzModule extends VuexModule {
  loaded = false;
  permissions = new Set<string>();
  lastLoadedAt: number | null = null;
  userId: string | null = null;
  /** Abilities version from GET /api/v1/me/abilities (not app settings version) */
  version: string | null = null;
  /** Working-unit DonViDefaultOptions from live abilities (not permissions-cached) */
  options: Record<string, string> = {};
  /** Effective id_don_vi from live abilities response */
  idDonVi: number | null = null;
  cache: Map<string, Set<string>> = new Map();

  // 5 minutes in milliseconds
  private readonly STALE_TIME = 5 * 60 * 1000;

  /**
   * Check if user has a specific permission
   */
  get can() {
    return (perm: string): boolean => {
      return this.permissions.has(perm);
    };
  }

  /**
   * Check if user has any of the given permissions
   */
  get canAny() {
    return (perms: string[]): boolean => {
      if (!perms || perms.length === 0) return true;
      return perms.some((perm) => this.permissions.has(perm));
    };
  }

  /**
   * Check if user has all of the given permissions
   */
  get canAll() {
    return (perms: string[]): boolean => {
      if (!perms || perms.length === 0) return true;
      return perms.every((perm) => this.permissions.has(perm));
    };
  }

  /**
   * Get all permissions as array
   */
  get allPermissions(): string[] {
    return Array.from(this.permissions);
  }

  /**
   * Check if permissions are stale (older than 5 minutes)
   */
  get isStale(): boolean {
    if (!this.lastLoadedAt) return true;
    return Date.now() - this.lastLoadedAt > this.STALE_TIME;
  }

  /**
   * Get a DonVi option value from the latest live abilities response
   */
  get getDonViOption() {
    return (key: string): string | undefined => {
      return this.options[key];
    };
  }

  /**
   * Working-unit BBGT approval level from options.OPT_BBGT_REQUIRED_APPROVAL.
   * 0 = none, 1 = approve only, 2 = confirm + approve
   */
  get workingUnitBBGTApprovalLevel(): number {
    const value = this.options.OPT_BBGT_REQUIRED_APPROVAL;
    if (value === undefined || value === null) {
      return 0;
    }
    return parseInt(value as string, 10) || 0;
  }

  @Mutation
  [Mutations.SET_DON_VI_OPTIONS](options: Record<string, string>) {
    this.options = options && typeof options === 'object' ? { ...options } : {};
  }

  @Mutation
  [Mutations.SET_ABILITIES_ID_DON_VI](idDonVi: number | null) {
    this.idDonVi = idDonVi;
  }

  @Mutation
  [Mutations.SET_ABILITIES](permissions: string[]) {
    this.permissions = new Set(permissions || []);
    this.lastLoadedAt = Date.now();

    if (this.userId !== null || this.version !== null) {
      const cacheKey = getCacheKey(this.userId, this.version);
      this.cache.set(cacheKey, this.permissions);
    }
  }

  @Mutation
  [Mutations.SET_ABILITIES_LOADED](loaded: boolean) {
    this.loaded = loaded;
  }

  @Mutation
  [Mutations.SET_USER_ID](userId: string | null) {
    this.userId = userId;
  }

  @Mutation
  [Mutations.SET_VERSION](version: string | null) {
    this.version = version;
  }

  /**
   * Ensure abilities are loaded. Deduplicates concurrent calls.
   * Skips the API when already loaded unless force=true.
   * @param force - Force reload even if already loaded
   */
  @Action({ rawError: true })
  async [Actions.LOAD_ABILITIES](force = false) {
    const context = this.context;
    const moduleState = context.state as AuthzModule;

    if (moduleState.loaded && !force) {
      return Promise.resolve();
    }

    await runAbilitiesTask(context, moduleState, { force });
  }

  /**
   * Ensure abilities are loaded (same as LOAD_ABILITIES).
   * Kept for callers that previously synced before every request;
   * does not hit the API when already loaded for the current page/session.
   */
  @Action({ rawError: true })
  async [Actions.SYNC_ABILITIES]() {
    await this.context.dispatch(Actions.LOAD_ABILITIES, false);
  }

  /**
   * Force refresh abilities (reload from API)
   */
  @Action({ rawError: true })
  async [Actions.REFRESH_ABILITIES]() {
    await this.context.dispatch(Actions.LOAD_ABILITIES, true);
  }

  /**
   * Reset abilities cache (call on logout or user/version change)
   */
  @Action
  async [Actions.RESET_ABILITIES]() {
    const moduleState = this.context.state as AuthzModule;
    resetAbilitiesInMemoryState(this.context, moduleState);
    pendingLoadPromise = null;

    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('abilities:')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
}
